import React, { createContext, useContext, useEffect } from 'react'
// import { useController, useFormContext } from 'react-hook-form'

import TestIds from '../../../testIds'
import Input from '../../../components/Input'
import Modal from '../../../components/Modal'
import { StyledButton } from '../../cart/components/PickUpStoreModal'
import { SeedMix, SeedMixSchema } from '../types/SeedOrderFormType'
import { useSelector } from 'react-redux'
import { selectCurrentFarmerAccount } from '../../azure-b2c/storage'
import { useAppDispatch } from '../../../store/rootStore'
import { setSeedValue } from '../storage/seedCart.slice'
import _ from 'lodash'

const RenameSeedMixModalContext = createContext<
  | {
      setRenamerOpen: React.Dispatch<React.SetStateAction<boolean>>
      setSeedMix: (_seedMix: SeedMix | undefined) => void
    }
  | undefined
>(undefined)

export const useSeedEditPriceModal = () => {
  const context = useContext(RenameSeedMixModalContext)
  if (!context) {
    throw new Error('useSeedEditPriceModal must be used within a RenameSeedMixProvider')
  }
  return context
}

type SeedEditPriceProviderProps = {
  children?: React.ReactNode
}

export const RenameSeedMixProvider: React.FC<SeedEditPriceProviderProps> = ({ children }) => {
  const [renamerOpen, setRenamerOpen] = React.useState(false)
  const [seedMix, setSeedMix] = React.useState<SeedMix>()

  const account = useSelector(selectCurrentFarmerAccount)
  const accountNumber = account?.accountNumber

  const dispatch = useAppDispatch()

  const [error, setError] = React.useState<string | undefined>(undefined)
  const [tempName, setTempName] = React.useState('')

  useEffect(() => {
    if (seedMix) {
      setTempName(seedMix.name)
      const copy = _.cloneDeep(seedMix)
      copy.name = tempName
      const valid = SeedMixSchema.safeParse(copy)
      const possibleError = valid.error?.formErrors?.fieldErrors?.name?.[0]
      if (possibleError) {
        console.log(possibleError)
        setError(possibleError)
      }
    } else {
      setTempName('')
      setError(undefined)
    }
  }, [seedMix])

  const handleCloseModal = () => {
    if (!seedMix) {
      return
    }
    if (tempName.length === 0) {
      setError('A seed order name is required')
      return
    }
    const copy = _.cloneDeep(seedMix)
    copy.name = tempName
    const valid = SeedMixSchema.safeParse(copy)
    const possibleError = valid.error?.formErrors?.fieldErrors?.name?.[0]
    if (possibleError) {
      setError(possibleError)
    } else if (tempName && accountNumber) {
      setError(undefined)
      dispatch(
        setSeedValue({
          accountNumber,
          key: `name`,
          value: tempName,
          seedMix,
        }),
      )
      setSeedMix(undefined)
      setRenamerOpen(false)
    }
  }

  const value = React.useMemo(() => {
    return {
      setRenamerOpen,
      setSeedMix: (_seedMix: SeedMix | undefined) => {
        setTempName(_seedMix?.name ?? '')
        setSeedMix(_seedMix)
      },
    }
  }, [setRenamerOpen, setSeedMix])

  const footer = React.useMemo(() => {
    return (
      <StyledButton
        data-testid={TestIds.feat.seed.name.closeButton}
        variant='contained'
        onClick={handleCloseModal}
      >
        Save changes
      </StyledButton>
    )
  }, [handleCloseModal])

  return (
    <RenameSeedMixModalContext.Provider value={value}>
      {children}
      <Modal
        title='Rename seed order'
        open={renamerOpen}
        handleClose={handleCloseModal}
        footer={footer}
        testId={TestIds.feat.seed.name.input}
        maxWidth='md'
      >
        <Input
          label='Seed order Name'
          error={!!error}
          errorMessage={error ? 'A seed order name is required' : undefined}
          type='text'
          testId={TestIds.feat.seed.paddock.nameInput}
          required
          value={tempName}
          onChange={(event) => {
            setTempName(event.currentTarget.value)
          }}
        />
      </Modal>
    </RenameSeedMixModalContext.Provider>
  )
}

export default RenameSeedMixProvider
